import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ukDate } from "@/lib/format";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, CalendarDays, Users, Scissors, UserCog, Settings as SettingsIcon,
  Shield, LogOut, Menu, X, Link2, Sparkles, Star, Inbox as InboxIcon, Gift,
} from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", roles: ["owner", "staff", "admin"] },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar-link", roles: ["owner", "staff"] },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients-link", roles: ["owner", "staff"] },
  { to: "/services", label: "Services", icon: Scissors, testid: "nav-services-link", roles: ["owner"] },
  { to: "/staff", label: "Staff", icon: UserCog, testid: "nav-staff-link", roles: ["owner"] },
  { to: "/reviews", label: "Reviews", icon: Star, testid: "nav-reviews-link", roles: ["owner"] },
  { to: "/requests", label: "Requests & forms", icon: InboxIcon, testid: "nav-requests-link", roles: ["owner"] },
  { to: "/sales", label: "Vouchers & courses", icon: Gift, testid: "nav-sales-link", roles: ["owner"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings-link", roles: ["owner"] },
  { to: "/admin", label: "Platform Admin", icon: Shield, testid: "nav-admin-link", roles: ["admin"] },
];

function ImpersonationBanner({ user }) {
  if (!user?.impersonated_by) return null;
  const back = async () => { await api.post("/auth/stop-impersonation"); window.location.href = "/admin"; };
  return (
    <div data-testid="impersonation-banner" className="px-4 py-2 bg-violet-700 text-white text-xs sm:text-sm flex items-center justify-center gap-3">
      <span>Super admin view — you're signed in as {user.email}</span>
      <button data-testid="return-to-admin" onClick={back} className="bg-white text-violet-800 font-semibold rounded-full px-3 py-0.5">Return to admin</button>
    </div>
  );
}

function TrialBanner({ business }) {
  const sub = business?.subscription;
  if (!sub) return null;
  let text = "", tone = "bg-[#D4A373] text-stone-900";
  if (sub.status === "trial") {
    const days = Math.max(0, Math.ceil((new Date(sub.trial_ends_at) - new Date()) / 86400000));
    text = `Free trial · ${days} day${days === 1 ? "" : "s"} left${sub.card ? ` · card saved, first payment ${ukDate(sub.trial_ends_at)}` : " · add a card in Settings → Subscription"}`;
  } else if (sub.status === "active") {
    text = `Active · renews ${ukDate(sub.current_period_end)}`;
    tone = "bg-emerald-600 text-white";
  } else if (sub.status === "past_due") {
    text = "Payment failed — please update your card in Settings → Subscription"; tone = "bg-rose-600 text-white";
  } else if (sub.status === "expired" || sub.status === "suspended") {
    text = "Your booking page is paused — add a card in Settings → Subscription to reactivate"; tone = "bg-rose-700 text-white";
  } else if (sub.status === "free") {
    return null;
  } else if (sub.status === "cancelled") {
    text = "Subscription cancelled"; tone = "bg-stone-700 text-white";
  }
  return (
    <div data-testid="subscription-trial-banner" className={cn("px-4 py-2 text-center text-xs sm:text-sm font-medium flex items-center justify-center gap-2", tone)}>
      <Sparkles className="h-3.5 w-3.5" /> {text}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, business, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user?.role));
  const bookingUrl = business ? `${window.location.origin}/${business.slug}` : "";

  const doLogout = async () => { await logout(); navigate("/login"); };

  const Sidebar = (
    <div className="flex h-full flex-col bg-[#18181B] text-zinc-100 w-64">
      <div className="px-6 py-6 border-b border-zinc-800">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-[#D4A373] flex items-center justify-center text-stone-900 font-bold">G</div>
          <div>
            <div className="font-bold text-[15px] leading-tight">Get Booked In</div>
            <div className="text-[11px] text-zinc-400 truncate max-w-[150px]">{business?.name || "Platform"}</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((n) => {
          const active = location.pathname === n.to;
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} data-testid={n.testid} onClick={() => setOpen(false)}
              className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                active ? "bg-[#D4A373] text-stone-900" : "text-zinc-300 hover:bg-zinc-800 hover:text-white")}>
              <Icon className="h-[18px] w-[18px]" /> {n.label}
            </Link>
          );
        })}
      </nav>
      {business && (
        <div className="px-3 pb-3">
          <button data-testid="copy-booking-link-button"
            onClick={() => { navigator.clipboard?.writeText(bookingUrl); toast.success("Booking link copied"); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors">
            <Link2 className="h-4 w-4 shrink-0" /> <span className="truncate">Copy booking link</span>
          </button>
        </div>
      )}
      <div className="px-3 pb-4 border-t border-zinc-800 pt-3">
        <div className="px-3 py-1.5 text-xs text-zinc-400">{user?.name} · <span className="capitalize">{user?.role}</span></div>
        <button data-testid="logout-button" onClick={doLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors">
          <LogOut className="h-[18px] w-[18px]" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 z-30">{Sidebar}</aside>
      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative z-50">{Sidebar}
            <button className="absolute top-5 -right-11 text-white" onClick={() => setOpen(false)}><X className="h-6 w-6" /></button>
          </div>
        </div>
      )}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <ImpersonationBanner user={user} />
        <TrialBanner business={business} />
        <header className="lg:hidden sticky top-0 z-20 bg-[#18181B] text-white px-4 py-3 flex items-center justify-between">
          <button data-testid="mobile-menu-button" onClick={() => setOpen(true)}><Menu className="h-6 w-6" /></button>
          <span className="font-bold">Get Booked In</span>
          <span className="w-6" />
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">{children}</main>
      </div>
    </div>
  );
}
